/**
 * Toast notification context and provider
 * Global toast state management for app-wide notifications
 */

import { createContext, useContext, useState, useCallback } from 'react';

const ToastContext = createContext(null);

// Toast types with their configurations
const TOAST_TYPES = {
  success: {
    icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
    className: 'bg-green-50 border-green-200 text-green-800',
    iconClass: 'text-green-500',
  },
  error: {
    icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
      </svg>
    ),
    className: 'bg-red-50 border-red-200 text-red-800',
    iconClass: 'text-red-500',
  },
  warning: {
    icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
      </svg>
    ),
    className: 'bg-amber-50 border-amber-200 text-amber-800',
    iconClass: 'text-amber-500',
  },
  info: {
    icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z" />
      </svg>
    ),
    className: 'bg-blue-50 border-blue-200 text-blue-800',
    iconClass: 'text-blue-500',
  },
};

// Individual Toast component
function Toast({ id, message, type = 'info', onDismiss, requireConfirm = false, centered = false }) {
  const config = TOAST_TYPES[type] || TOAST_TYPES.info;

  if (centered) {
    return (
      <div
        className={`flex flex-col items-center gap-4 p-6 rounded-xl border shadow-2xl max-w-md w-full ${config.className}`}
        role="alert"
        aria-live="assertive"
      >
        <span className={`flex-shrink-0 ${config.iconClass}`}>
          <span className="block w-10 h-10">{config.icon && (
            <svg className="w-10 h-10" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              {type === 'success' && (
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              )}
              {type === 'error' && (
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
              )}
              {type === 'warning' && (
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
              )}
              {type === 'info' && (
                <path strokeLinecap="round" strokeLinejoin="round" d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z" />
              )}
            </svg>
          )}</span>
        </span>
        <p className="text-center text-base font-medium">{message}</p>
        <button
          onClick={() => onDismiss(id)}
          className="px-6 py-2 rounded-lg bg-white border border-current text-sm font-semibold hover:bg-black/5 transition-colors min-w-[100px]"
          autoFocus
        >
          OK
        </button>
      </div>
    );
  }

  return (
    <div
      className={`flex items-start gap-3 p-4 rounded-lg border shadow-lg backdrop-blur-sm animate-slide-in-right ${config.className}`}
      role="alert"
      aria-live="polite"
    >
      <span className={`flex-shrink-0 ${config.iconClass}`}>{config.icon}</span>
      <p className="flex-1 text-sm font-medium">{message}</p>
      {requireConfirm ? (
        <button
          onClick={() => onDismiss(id)}
          className="flex-shrink-0 px-3 py-1 rounded bg-white border border-current text-xs font-semibold hover:bg-black/5 transition-colors"
        >
          OK
        </button>
      ) : (
        <button
          onClick={() => onDismiss(id)}
          className="flex-shrink-0 p-0.5 rounded hover:bg-black/5 transition-colors"
          aria-label="Dismiss notification"
        >
          <svg className="w-4 h-4 opacity-60" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      )}
    </div>
  );
}

// Toast Container - renders all active toasts
function ToastContainer({ toasts, onDismiss }) {
  const cornerToasts = toasts.filter((t) => !t.centered);
  const centeredToasts = toasts.filter((t) => t.centered);

  return (
    <>
      {cornerToasts.length > 0 && (
        <div
          className="fixed top-4 right-4 z-[9999] flex flex-col gap-2 max-w-sm w-full pointer-events-none"
          aria-label="Notifications"
        >
          {cornerToasts.map((toast) => (
            <div key={toast.id} className="pointer-events-auto">
              <Toast {...toast} onDismiss={onDismiss} />
            </div>
          ))}
        </div>
      )}
      {centeredToasts.length > 0 && (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/30 backdrop-blur-sm"
          aria-label="Confirmation"
        >
          <div className="flex flex-col gap-3 w-full max-w-md">
            {centeredToasts.map((toast) => (
              <Toast key={toast.id} {...toast} onDismiss={onDismiss} />
            ))}
          </div>
        </div>
      )}
    </>
  );
}

// Toast Provider
export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);

  const removeToast = useCallback((id) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  const showToast = useCallback((message, type = 'info', duration = 5000, options = {}) => {
    const id = Date.now() + Math.random();
    const newToast = {
      id,
      message,
      type,
      centered: !!options.centered,
      requireConfirm: !!options.requireConfirm,
    };

    setToasts((prev) => [...prev, newToast]);

    // Auto-dismiss after duration (centered/requireConfirm default to no auto-dismiss)
    const effectiveDuration =
      newToast.centered || newToast.requireConfirm ? 0 : duration;
    if (effectiveDuration > 0) {
      setTimeout(() => {
        removeToast(id);
      }, effectiveDuration);
    }

    return id;
  }, [removeToast]);

  // Convenience methods
  const toast = {
    show: showToast,
    showToast,               // alias used by many components
    success: (message, duration) => showToast(message, 'success', duration),
    error: (message, duration) => showToast(message, 'error', duration),
    warning: (message, duration) => showToast(message, 'warning', duration),
    info: (message, duration) => showToast(message, 'info', duration),
    // Centered modal-style toast with OK button - requires user confirmation
    confirm: (message, type = 'success') =>
      showToast(message, type, 0, { centered: true, requireConfirm: true }),
    dismiss: removeToast,
    dismissAll: () => setToasts([]),
  };

  return (
    <ToastContext.Provider value={toast}>
      {children}
      <ToastContainer toasts={toasts} onDismiss={removeToast} />
    </ToastContext.Provider>
  );
}

// Hook to use toast notifications
export function useToast() {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
}

export default ToastContext;
